import { useState, useCallback } from "react";
import styles from "./RegistrationForm.module.css";
import { addNewCompetition } from "./CompetitionAction";
import toast from "react-hot-toast";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import razorQr from "../../assets/images/razorpay.jpg"

// Constants
const INITIAL_FORM_STATE = {
  fullName: "",
  class: "",
  schoolName: "",
  parentPhone: "",
  parentEmail: "",
  city: "",
  category: "FICTION",
  paymentScreenshot: null,
  agreeTerms: false,
};

export default function RegistrationForm() {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  const {
    fullName,
    class: studentClass,
    schoolName,
    parentPhone,
    parentEmail,
    city,
    category,
    paymentScreenshot,
    agreeTerms,
  } = formData;

  const handleInputChange = useCallback((e) => {
    const { name, value, type, files, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }));
  }, []);

  const validateForm = () => {
    if (!paymentScreenshot) {
      toast.error("Please upload your payment screenshot.");
      return false;
    }
    if (!agreeTerms) {
      toast.error("You must agree to the terms and conditions.");
      return false;
    }
    if (paymentScreenshot && !paymentScreenshot.type.startsWith("image/")) {
      toast.error("Payment screenshot must be an image file.");
      return false;
    }
    return true;
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) return;

      try {
        const { paymentScreenshot, ...itemData } = formData;
        const res = await addNewCompetition(itemData, paymentScreenshot);

        if (res.success) {
          toast.success("Registration successful!");
          setFormData(INITIAL_FORM_STATE);
        } else {
          toast.error(res.message || "Something went wrong!");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to submit form.");
      }
    },
    [formData]
  );

  const renderFormField = (label, name, type, placeholder, value, required = true) => (
    <div className={styles.formGroup}>
      <label htmlFor={name} className={styles.label}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        className={styles.input}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        required={required}
      />
    </div>
  );

  const renderSelectField = (label, name, value, options, required = true) => (
    <div className={styles.formGroup}>
      <label htmlFor={name} className={styles.label}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      <select
        id={name}
        name={name}
        className={styles.select}
        value={value}
        onChange={handleInputChange}
        required={required}
      >
        <option value="">Select</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <div className={styles.formCard}>
          <form className={styles.form} onSubmit={handleSubmit}>
            {renderFormField("Full Name", "fullName", "text", "Your full name", fullName)}
            {renderSelectField(
              "Class (3-12)",
              "class",
              studentClass,
              Array.from({ length: 10 }, (_, i) => ({
                value: i + 3,
                label: `Class ${i + 3}`,
              }))
            )}
            {renderFormField("School Name", "schoolName", "text", "Your school name", schoolName)}

            <div className={styles.formRow}>
              {renderFormField("Parent's Phone", "parentPhone", "tel", "+91 9876543210", parentPhone)}
              {renderFormField("Parent's Email", "parentEmail", "email", "parent@example.com", parentEmail)}
            </div>

            {renderFormField("City", "city", "text", "Your city", city)}

            {/* Category */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Choose Category <span className={styles.required}>*</span>
              </label>
              <div className={styles.radioGroup}>
                {["FICTION", "NON_FICTION"].map((cat) => (
                  <div key={cat} className={styles.radioOption}>
                    <input
                      type="radio"
                      id={cat}
                      name="category"
                      value={cat}
                      className={styles.radioInput}
                      checked={category === cat}
                      onChange={handleInputChange}
                    />
                    <label htmlFor={cat}>{cat.replace("_", " ")}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Screenshot */}
            <div className={styles.paymentSection}>
              <h3 className={styles.paymentTitle}>Payment: ₹100 (refunded after submission)</h3>
              <img src={razorQr} alt="QR Code" width={200} height={200} className={styles.qrCode} />
              <p className={styles.qrText}>
                Scan to pay ₹100 (fully refundable after submission)
              </p>
              <div className={styles.fileUpload}>
                <label htmlFor="paymentScreenshot" className={styles.label}>
                  Upload Payment Screenshot
                </label>
                <input
                  type="file"
                  id="paymentScreenshot"
                  name="paymentScreenshot"
                  className={styles.fileInput}
                  accept="image/*"
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Terms */}
            <div className={styles.checkbox}>
              <input
                type="checkbox"
                id="agreeTerms"
                name="agreeTerms"
                className={styles.checkboxInput}
                checked={agreeTerms}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="agreeTerms" className={styles.checkboxLabel}>
                I agree to the{" "}
                <a href="#" className={styles.link}>
                  Terms & Conditions
                </a>{" "}
                and allow Bookalooza to contact me.
              </label>
            </div>

            {/* Submit */}
            <PrimaryButton
              type="submit"
              // className={styles.submitButton}
              disabled={!agreeTerms}
            >
              🚀 Let's Get Started!
            </PrimaryButton>
          </form>
        </div>
      </div>
    </main>
  );
}
